import React from 'react';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  FlattenedTask,
  TimelineConfig,
  TaskLevelColors,
  CriticalPathColor,
  BAR_HEIGHT,
} from '../types/gantt.types';
import { calculateTaskBarPosition } from '../utils/dateUtils';

interface TaskBarProps {
  task: FlattenedTask;
  config: TimelineConfig;
  isCritical: boolean;
  onClick?: (task: FlattenedTask) => void;
}

const TaskBar: React.FC<TaskBarProps> = ({ task, config, isCritical, onClick }) => {
  const position = calculateTaskBarPosition(task, config);
  const progress = task.progress || 0;
  const progressWidth = (position.width * progress) / 100;

  const barColor = isCritical ? CriticalPathColor : TaskLevelColors[task.level];

  const tooltipContent = (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{task.name}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Start:</span>
        <span>{dayjs(task.startDate).format('DD-MMM-YYYY')}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>End:</span>
        <span>{dayjs(task.endDate).format('DD-MMM-YYYY')}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Actual Start:</span>
        <span>
          {task.actualStartDate
            ? dayjs(task.actualStartDate).format('DD-MMM-YYYY')
            : '-'}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Actual End:</span>
        <span>
          {task.actualEndDate
            ? dayjs(task.actualEndDate).format('DD-MMM-YYYY')
            : '-'}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Duration:</span>
        <span>{task.duration} days</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Progress:</span>
        <span>{Math.round(progress)}%</span>
      </div>
      {isCritical && (
        <div
          style={{
            marginTop: 4,
            paddingTop: 4,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            color: '#ff7875',
          }}
        >
          Critical Path
        </div>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" color="rgba(0,0,0,0.85)">
      <g
        className="gantt-task-bar"
        style={{ cursor: 'pointer' }}
        onClick={() => onClick?.(task)}
      >
        {/* Background bar */}
        <rect
          x={position.x}
          y={position.y}
          width={position.width}
          height={BAR_HEIGHT}
          rx={4}
          ry={4}
          fill={barColor}
          opacity={0.9}
        />

        {/* Progress fill */}
        {progress > 0 && (
          <rect
            x={position.x}
            y={position.y}
            width={progressWidth}
            height={BAR_HEIGHT}
            rx={4}
            ry={4}
            fill="rgba(255,255,255,0.3)"
          />
        )}

        {/* Task name label (if bar is wide enough) */}
        {position.width > 60 && (
          <text
            x={position.x + 8}
            y={position.y + BAR_HEIGHT / 2}
            fill="#fff"
            fontSize={11}
            dominantBaseline="middle"
            style={{ pointerEvents: 'none' }}
          >
            {task.name.length > position.width / 8
              ? task.name.substring(0, Math.floor(position.width / 8) - 2) + '...'
              : task.name}
          </text>
        )}
      </g>
    </Tooltip>
  );
};

export default TaskBar;
